import { useState, useEffect, useCallback } from "react";
import { employeeApi } from "../services/api";
import { useLocationContext } from "../contexts/LocationContext";
import { useTenantContext } from "../contexts/TenantContext";

/**
 * Helper to extract data from API response
 * Handles both { success, data } and { Success, Data } formats
 */
const extractData = (response) => {
  if (!response) return { success: false, data: null };

  // Handle both casing conventions
  const success = response.success ?? response.Success ?? false;
  const data = response.data ?? response.Data ?? null;
  const message = response.message ?? response.Message ?? null;

  return { success, data, message };
};

/**
 * Hook for fetching and managing employees
 * Automatically filters by current location if one is selected
 * For Global Admins, also filters by selected tenant
 */
export function useEmployees(options = {}) {
  const {
    activeOnly = false,
    troncEligibleOnly = false,
    locationId: explicitLocationId,
    autoFetch = true,
    useCurrentLocation = true,
  } = options;
  const { currentLocationId } = useLocationContext();
  const { currentTenantId } = useTenantContext();

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use explicit locationId if provided, otherwise use current location context
  const effectiveLocationId =
    explicitLocationId ?? (useCurrentLocation ? currentLocationId : null);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (activeOnly) params.activeOnly = true;
      if (troncEligibleOnly) params.troncEligibleOnly = true;
      if (effectiveLocationId) params.locationId = effectiveLocationId;
      // For Global Admins, filter by selected tenant
      if (currentTenantId) params.tenantId = currentTenantId;

      const response = await employeeApi.getAll(params);
      const { success, data, message } = extractData(response);

      if (success) {
        setEmployees(data || []);
      } else {
        setError(message || "Failed to fetch employees");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch employees");
    } finally {
      setLoading(false);
    }
  }, [activeOnly, troncEligibleOnly, effectiveLocationId, currentTenantId]);

  useEffect(() => {
    if (autoFetch) {
      fetchEmployees();
    }
  }, [fetchEmployees, autoFetch]);

  return {
    employees,
    loading,
    error,
    refetch: fetchEmployees,
    locationId: effectiveLocationId,
    tenantId: currentTenantId,
  };
}

/**
 * Hook for fetching a single employee
 */
export function useEmployee(id) {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEmployee = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await employeeApi.getById(id);
      const { success, data, message } = extractData(response);

      if (success) {
        setEmployee(data);
      } else {
        setError(message || "Failed to fetch employee");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch employee");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEmployee();
  }, [fetchEmployee]);

  return {
    employee,
    loading,
    error,
    refetch: fetchEmployee,
  };
}

/**
 * Hook for employee mutations (create, update, delete)
 */
export function useEmployeeMutations() {
  const { currentLocationId } = useLocationContext();
  const { currentTenantId } = useTenantContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createEmployee = async (employeeData) => {
    setLoading(true);
    setError(null);

    try {
      // Ensure locationId is set if not provided
      const data = {
        ...employeeData,
        locationId: employeeData.locationId ?? currentLocationId,
      };
      const response = await employeeApi.create(data);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateEmployee = async (id, employeeData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await employeeApi.update(id, employeeData);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateEligibility = async (id, eligibilityData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await employeeApi.updateEligibility(id, eligibilityData);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteEmployee = async (id, reason) => {
    setLoading(true);
    setError(null);

    try {
      const response = await employeeApi.delete(id, reason);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const syncFromProvider = async (providerName) => {
    setLoading(true);
    setError(null);

    try {
      const response = await employeeApi.syncFromProvider(providerName);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const bulkUpdateEligibility = async (
    employeeIds,
    isEligibleForTronc,
    reason = null,
  ) => {
    setLoading(true);
    setError(null);

    try {
      const response = await employeeApi.bulkUpdateEligibility({
        employeeIds,
        isEligibleForTronc,
        reason:
          reason ||
          `Bulk update - ${isEligibleForTronc ? "made eligible" : "made ineligible"}`,
      });
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    createEmployee,
    updateEmployee,
    updateEligibility,
    deleteEmployee,
    syncFromProvider,
    bulkUpdateEligibility,
    currentLocationId,
    currentTenantId,
  };
}

export default useEmployees;
